import { ServerEvent } from "@malfak/contracts";
import { useEffect, useSyncExternalStore } from "react";
import { config } from "../config";
import { getCurrentOrgId } from "./client";

/**
 * Live updates over SSE (`GET /v1/orgs/:orgId/events`).
 *
 * fetch + a stream reader instead of EventSource: it sends the session cookie
 * (credentials: include) on any base URL, exposes the HTTP status (a 401 stops the
 * retries instead of hammering), and resumes with Last-Event-ID. Reconnects back off
 * exponentially with jitter, and pause while the browser is offline.
 */
export type LiveStatus = "idle" | "connecting" | "open" | "reconnecting" | "offline";
export type LiveEvent = ServerEvent;
type Listener = (e: LiveEvent) => void;

let status: LiveStatus = "idle";
const statusListeners = new Set<() => void>();
const listeners = new Set<Listener>();
let controller: AbortController | null = null;
let lastEventId: string | null = null;
let attempt = 0;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let lastEventAt = 0;

function setStatus(s: LiveStatus) {
  if (s === status) return;
  status = s;
  statusListeners.forEach((l) => l());
}

export const liveEvents = {
  status: () => status,
  lastEventAt: () => lastEventAt,
  subscribeStatus(l: () => void) {
    statusListeners.add(l);
    return () => statusListeners.delete(l);
  },
  on(l: Listener) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /** Emit locally (used by mock mode tests and optimistic flows). */
  emit(e: LiveEvent) {
    lastEventAt = Date.now();
    listeners.forEach((l) => l(e));
  },
  connect,
  disconnect,
};

export function parseSseChunk(buffer: string): { events: { id?: string; event?: string; data: string }[]; rest: string } {
  const events: { id?: string; event?: string; data: string }[] = [];
  const blocks = buffer.split(/\r?\n\r?\n/);
  const rest = blocks.pop() ?? "";
  for (const block of blocks) {
    const ev: { id?: string; event?: string; data: string } = { data: "" };
    const data: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (!line || line.startsWith(":")) continue;
      const idx = line.indexOf(":");
      const field = idx === -1 ? line : line.slice(0, idx);
      const value = idx === -1 ? "" : line.slice(idx + 1).replace(/^ /, "");
      if (field === "data") data.push(value);
      else if (field === "id") ev.id = value;
      else if (field === "event") ev.event = value;
    }
    if (data.length) {
      ev.data = data.join("\n");
      events.push(ev);
    }
  }
  return { events, rest };
}

function scheduleRetry() {
  clearTimeout(retryTimer);
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    setStatus("offline");
    return;
  }
  setStatus("reconnecting");
  const base = Math.min(30_000, 1000 * 2 ** attempt);
  attempt++;
  retryTimer = setTimeout(connect, base / 2 + Math.random() * (base / 2));
}

async function connect() {
  const orgId = getCurrentOrgId();
  if (!orgId) return;
  controller?.abort();
  const ctl = new AbortController();
  controller = ctl;
  setStatus(attempt === 0 ? "connecting" : "reconnecting");
  try {
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (lastEventId) headers["Last-Event-ID"] = lastEventId;
    const res = await fetch(`${config().apiBaseUrl}/v1/orgs/${orgId}/events`, { headers, credentials: "include", signal: ctl.signal });
    if (res.status === 401 || res.status === 403) {
      setStatus("idle");
      return;
    }
    if (!res.ok || !res.body) throw new Error(`SSE ${res.status}`);
    attempt = 0;
    setStatus("open");
    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      const { events, rest } = parseSseChunk(buffer);
      buffer = rest;
      for (const raw of events) {
        if (raw.id) lastEventId = raw.id;
        let json: unknown;
        try {
          json = JSON.parse(raw.data);
        } catch {
          continue;
        }
        const parsed = ServerEvent.safeParse(json);
        if (parsed.success) liveEvents.emit(parsed.data);
        else if (import.meta.env.DEV) console.warn("[sse] unknown event", json);
      }
    }
    if (!ctl.signal.aborted) scheduleRetry();
  } catch {
    if (!ctl.signal.aborted) scheduleRetry();
  }
}

function disconnect() {
  clearTimeout(retryTimer);
  controller?.abort();
  controller = null;
  attempt = 0;
  setStatus("idle");
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (status === "offline") {
      attempt = 0;
      void connect();
    }
  });
  window.addEventListener("offline", () => {
    if (status !== "idle") setStatus("offline");
  });
}

export function useLiveStatus(): LiveStatus {
  return useSyncExternalStore(liveEvents.subscribeStatus, liveEvents.status, liveEvents.status);
}

/** Subscribe to live events for the lifetime of a component. */
export function useLiveEvent(handler: Listener, deps: unknown[] = []) {
  useEffect(() => {
    const off = liveEvents.on(handler);
    return () => {
      off();
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}
